import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LayoutModule, TableModule } from '@monsido/angular-shared-components';
import { TranslateModule } from 'app/modules/translate.module';
import { ConfirmModule } from '@monsido/confirm/confirm.module';
import { UserInterface } from '@monsido/modules/models/api/interfaces/user.interface';
import {
    BackendAdminDeleteUsersService,
    EmailSearchResult,
    EnvironmentResult,
} from './backend-admin-delete-users.service';

type FlowStep = 'input' | 'confirm-emails' | 'searching' | 'results' | 'deleting' | 'success';

@Component({
    selector: 'mon-backend-admin-delete-users',
    standalone: true,
    imports: [CommonModule, FormsModule, LayoutModule, TableModule, TranslateModule, ConfirmModule],
    templateUrl: './backend-admin-delete-users.component.html',
})
export class BackendAdminDeleteUsersComponent implements OnInit {
    currentStep: FlowStep = 'input';
    emailInput: string = '';
    extractedEmails: string[] = [];
    searchResults: EmailSearchResult[] = [];
    loading: boolean = false;
    isDeleting: boolean = false;
    searchProgress: number = 0;
    totalSearches: number = 0;

    constructor (private deleteUsersService: BackendAdminDeleteUsersService) {}

    ngOnInit (): void {
        this.currentStep = 'input';
    }

    // Step 1 -> Step 2: Extract emails and show confirmation
    extractEmails (): void {
        this.extractedEmails = this.deleteUsersService.extractEmailsFromText(this.emailInput);
        if (this.extractedEmails.length > 0) {
            this.currentStep = 'confirm-emails';
        }
    }

    // Step 2 -> Step 3: User confirms emails and starts search
    async confirmAndSearch (): Promise<void> {
        this.currentStep = 'searching';
        this.searchProgress = 0;
        this.searchResults = [];

        try {
            this.searchResults = await this.deleteUsersService.searchUsersAcrossEnvironments(
                this.extractedEmails,
                (progress, total) => {
                    this.searchProgress = progress;
                    this.totalSearches = total;
                },
            );
            this.currentStep = 'results';
        } catch (error) {
            this.currentStep = 'input';
        }
    }

    // Step 4 -> Step 5: User confirms deletion
    async confirmDeletion (): Promise<void> {
        const totalUsers = this.getTotalUsersFound();

        if (totalUsers === 0) {
            return;
        }

        this.currentStep = 'deleting';
        this.isDeleting = true;

        try {
            await this.deleteUsersService.deleteUsersFromAllEnvironments(this.searchResults);
            this.isDeleting = false;
            this.currentStep = 'success';
        } catch (error) {
            this.isDeleting = false;
            this.currentStep = 'results';
        }
    }

    // Navigation methods
    goBackToInput (): void {
        this.currentStep = 'input';
        this.extractedEmails = [];
        this.searchResults = [];
    }

    goBackToEmailConfirmation (): void {
        this.currentStep = 'confirm-emails';
        this.searchResults = [];
    }

    goBackToResults (): void {
        this.currentStep = 'results';
        this.isDeleting = false;
    }

    startOver (): void {
        this.currentStep = 'input';
        this.emailInput = '';
        this.extractedEmails = [];
        this.searchResults = [];
        this.loading = false;
        this.isDeleting = false;
        this.searchProgress = 0;
        this.totalSearches = 0;
    }

    getTotalUsersFound (): number {
        return this.searchResults.reduce((total, result) => total + result.totalUsers, 0);
    }

    getSearchProgressPercentage (): number {
        if (this.totalSearches === 0) {
            return 0;
        }
        return Math.round((this.searchProgress / this.totalSearches) * 100);
    }

    getTotalEnvironments (): number {
        return this.deleteUsersService.getTotalEnvironments();
    }

    getUserDisplayName (user: UserInterface): string {
        if (user.first_name && user.last_name) {
            return `${user.first_name} ${user.last_name}`;
        }
        if (user.first_name) {
            return user.first_name;
        }
        if (user.last_name) {
            return user.last_name;
        }
        return 'N/A';
    }

    getEmailsWithUsers (): EmailSearchResult[] {
        return this.searchResults.filter(emailResult => emailResult.totalUsers > 0);
    }

    getEmailsWithoutUsers (): string[] {
        return this.searchResults
            .filter(emailResult => emailResult.totalUsers === 0)
            .map(emailResult => emailResult.email);
    }

    getEnvironmentsWithUsers (emailResult: EmailSearchResult): EnvironmentResult[] {
        return emailResult.environments.filter(env => env.users.length > 0);
    }

    private extractEmailsFromText (text: string): string[] {
        const emails: string[] = [];

        // Simple email regex to find any email addresses in the text
        const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
        const matches = text.match(emailRegex);

        if (matches) {
            for (const email of matches) {
                const cleanEmail = email.trim();
                if (cleanEmail && !emails.includes(cleanEmail)) {
                    emails.push(cleanEmail);
                }
            }
        }

        return emails;
    }
}
